import { COORDINATE_QUANTUM_MM, EPSILON, MAX_PERPEND_GAP_MM } from './constants.js';
import { fail } from './errors.js';

/**
 * A length in millimetres.
 *
 * DESIGN NOTE (deviation from design document B2): the design proposes
 * `Mm`/`Px` as `unique symbol` brands. Inside the core there is exactly one
 * unit, so a nominal brand would force a cast on every arithmetic result
 * (`x + width` is a plain `number`) while catching nothing. The brand earns
 * its place at the render boundary, where millimetres and pixels genuinely
 * mix; `@bw/render-2d` should declare the branded `Px` and the transform
 * functions that convert between them. Here `Mm` documents intent.
 */
export type Mm = number;

/** Round half to even, matching the reference implementation's `round()`. */
export function roundHalfToEven(value: number): number {
  const floor = Math.floor(value);
  const remainder = value - floor;
  if (remainder > 0.5) return floor + 1;
  if (remainder < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * Round a world coordinate to the editor's eighth-millimetre precision
 * (clause 2.2). Ties resolve half-to-even so repeated quantisation is
 * idempotent.
 */
export function quantise(value: number, quantum: number = COORDINATE_QUANTUM_MM): Mm {
  if (!Number.isFinite(value)) fail('Coordinate is not finite');
  const scaled = value / quantum;
  if (!Number.isFinite(scaled) || Math.abs(scaled) > Number.MAX_SAFE_INTEGER) {
    fail('Coordinate is too large');
  }
  const result = roundHalfToEven(scaled) * quantum;
  // -0 is a valid double but reads badly everywhere downstream.
  return result === 0 ? 0 : result;
}

/** True when a value already sits exactly on the coordinate lattice. */
export function isQuantised(value: number): boolean {
  if (!Number.isFinite(value)) return false;
  return Math.abs(value - quantise(value)) <= EPSILON;
}

/**
 * Format a dimension for display (clause 2.3): round to 3 decimal places,
 * then print with C's `%g` semantics at 6 significant figures.
 */
export function formatMm(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  let rounded = roundHalfToEven(value * 1000) / 1000;
  if (rounded === 0) rounded = 0;
  return generalFormat(rounded, 6);
}

function generalFormat(value: number, precision: number): string {
  if (value === 0) return '0';
  const exponent = Number.parseInt(value.toExponential().split('e')[1] as string, 10);
  if (exponent < -4 || exponent >= precision) {
    const [mantissa, exp] = value.toExponential(precision - 1).split('e') as [string, string];
    const trimmed = mantissa.includes('.')
      ? mantissa.replace(/0+$/, '').replace(/\.$/, '')
      : mantissa;
    const sign = exp.startsWith('-') ? '-' : '+';
    return `${trimmed}e${sign}${exp.replace(/^[+-]/, '').padStart(2, '0')}`;
  }
  const fixed = value.toFixed(Math.max(0, precision - 1 - exponent));
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
}

/** The two supported orientations. */
export type Rotation = 0 | 90;

/**
 * Return the supported orientation (clause 2.4). Any integer is reduced
 * modulo 180; 180 becomes 0, 270 and -90 become 90, 45 is rejected.
 */
export function normaliseRotation(rotation: number): Rotation {
  if (typeof rotation !== 'number' || !Number.isInteger(rotation)) {
    fail('Rotation must be 0 or 90 degrees');
  }
  const value = ((rotation % 180) + 180) % 180;
  if (value !== 0 && value !== 90) fail('Rotation must be 0 or 90 degrees');
  return value;
}

/** Return a positive perpend joint quantised to an eighth millimetre (clause 4.1). */
export function normalisePerpendGap(value: number | string): Mm {
  if (typeof value === 'boolean') fail('Perpend joint spacing must be a positive number');
  const spacing = typeof value === 'string' ? Number(value.trim()) : value;
  if (typeof spacing !== 'number' || Number.isNaN(spacing) || (typeof value === 'string' && value.trim() === '')) {
    fail('Perpend joint spacing must be a positive number');
  }
  if (!Number.isFinite(spacing)) fail('Perpend joint spacing must be finite');
  if (spacing > MAX_PERPEND_GAP_MM + EPSILON) {
    fail(`Perpend joint spacing cannot exceed ${formatMm(MAX_PERPEND_GAP_MM)} mm`);
  }
  let quantised: number;
  try {
    quantised = quantise(spacing);
  } catch {
    fail('Perpend joint spacing is too large');
  }
  if (quantised < COORDINATE_QUANTUM_MM - EPSILON) {
    fail(`Perpend joint spacing must be at least ${formatMm(COORDINATE_QUANTUM_MM)} mm`);
  }
  return quantised;
}

/** A material reference. Opaque to the core — see `catalogue.ts` notes. */
export type MaterialId = string;

export function normaliseMaterialId(value: unknown): MaterialId {
  if (typeof value !== 'string' || value.length === 0) {
    fail('Brick material must be a non-empty identifier');
  }
  return value;
}
